import React, { useEffect, useState, useCallback } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { ActivityProvider } from "./contexts/ActivityContext";
import { PreferencesProvider } from "./contexts/PreferencesContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { QuickIslandProvider } from "./contexts/QuickIslandContext";
import { Header } from "./components/Layout/Header";
import { ActivityBar } from "./components/Layout/ActivityBar";
import { MapContainer } from "./components/Layout/MapContainer";
import { MapSyncWrapper } from "./components/Map/MapSyncWrapper";
import { DebugConsole } from "./components/Debug/DebugConsole";
import { SettingsModal } from "./components/Settings/SettingsModal";
import { PersistentQuickIsland } from "./components/common/PersistentQuickIsland";
import { AlertPopover } from "./components/common/AlertPopover";
import { CameraOverlay } from "./components/Camera/CameraOverlay";
import { getTrackingService } from "./services/trackingService";
import { useQuickIsland } from "./contexts/QuickIslandContext";
import { useActivity } from "./hooks/useActivity";
import { usePreferences } from "./hooks/usePreferences";
import { getCameraService } from "./services/cameraService";
import debugx from "debugx";
import { setDefaultDebugConfig } from "./utils/debugx";
import { wakeLockService } from "./services/wakeLockService";
import "./App.css";

// Set default debug configuration at module level (before any debugx calls)
setDefaultDebugConfig("-*{stack:hide map link;throttle:auto 2000ms;}");

const debug = debugx("bikedash:app");

// Search busy indicator component
const SearchBusyIndicator: React.FC = () => {
  const { searchBusy } = useQuickIsland();

  if (!searchBusy) return null;

  return (
    <div className="search-busy-indicator">
      <div className="search-busy-indicator-spinner" />
      <div className="search-busy-indicator-text">Processing trails...</div>
    </div>
  );
};

// Text size applier component
const TextSizeApplier: React.FC = () => {
  const { userPreferences } = usePreferences();

  useEffect(() => {
    const textSize = userPreferences.textSize ?? "medium";
    document.documentElement.setAttribute("data-text-size", textSize);
    debug("Applied text size:", textSize);
  }, [userPreferences.textSize]);

  return null;
};

// Global event listener component that's always mounted
const GlobalSearchEventListener: React.FC = () => {
  const { setSearchBusy, setIsSearching } = useQuickIsland();

  useEffect(() => {
    const handleSearchProcessingStart = () => {
      debug("Global: Received searchProcessingStart event");
      setSearchBusy(true);
    };

    const handleSearchProcessingEnd = () => {
      debug("Global: Received searchProcessingEnd event");
      setSearchBusy(false);
    };

    const handleSearchBusyStart = () => {
      debug("Global: Received searchBusyStart event");
      setIsSearching(true);
    };

    const handleSearchBusyEnd = () => {
      debug("Global: Received searchBusyEnd event");
      setIsSearching(false);
    };

    window.addEventListener(
      "searchProcessingStart",
      handleSearchProcessingStart,
    );
    window.addEventListener("searchProcessingEnd", handleSearchProcessingEnd);
    window.addEventListener("searchBusyStart", handleSearchBusyStart);
    window.addEventListener("searchBusyEnd", handleSearchBusyEnd);

    return () => {
      window.removeEventListener(
        "searchProcessingStart",
        handleSearchProcessingStart,
      );
      window.removeEventListener(
        "searchProcessingEnd",
        handleSearchProcessingEnd,
      );
      window.removeEventListener("searchBusyStart", handleSearchBusyStart);
      window.removeEventListener("searchBusyEnd", handleSearchBusyEnd);
    };
  }, [setSearchBusy, setIsSearching]);

  return null; // This component doesn't render anything
};

// Auto-pause alert component
const AutoPauseAlert: React.FC = () => {
  const { pauseReason, dismissAutoPause } = useActivity();

  const getMessage = () => {
    switch (pauseReason) {
      case "distance":
        return "Activity auto-paused: GPS jumped";
      case "inactivity":
        return "Activity auto-paused: Inactive";
      default:
        return "";
    }
  };

  return (
    <AlertPopover
      show={pauseReason === "distance" || pauseReason === "inactivity"}
      message={getMessage()}
      icon="⏸️"
      variant="info"
      onDismiss={dismissAutoPause}
    />
  );
};

// Camera overlay wrapper with context access
const CameraWrapper: React.FC = () => {
  const { openIsland } = useQuickIsland();

  return (
    <CameraOverlay
      onLongPress={() => openIsland("camera-controls")}
      onSwipeDown={() => openIsland("camera-controls")}
    />
  );
};

// GPS error alert component
const GPSErrorAlert: React.FC = () => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const trackingService = getTrackingService();

    // Subscribe to tracking data to clear error when we get valid GPS
    const dataSubscription = trackingService.subscribe((data) => {
      if (data.position && data.position.lat && data.position.lng) {
        setHasError(false);
        setErrorMessage("");
      }
    });

    // Subscribe to tracking errors
    const errorSubscription = trackingService.onError((error) => {
      if (error) {
        setHasError(true);
        setErrorMessage("GPS Error - Click To Retry");
        debug("GPSErrorAlert: Tracking error:", error.message);
      }
      // Note: Don't clear error here - only clear when we get valid GPS
    });

    return () => {
      dataSubscription.unsubscribe();
      errorSubscription.unsubscribe();
    };
  }, []);

  const handleRetry = async () => {
    debug("Attempting to restart tracking after GPS error");
    const trackingService = getTrackingService();
    // Clear error immediately to show we're trying
    setHasError(false);
    try {
      trackingService.clearError();
      await trackingService.start("auto");
      debug("Tracking restarted successfully");
    } catch (error) {
      debug.error("Failed to restart tracking:", error);
      // Re-set error if restart fails
      setHasError(true);
      setErrorMessage("GPS Error - Click To Retry");
    }
  };

  const handleDismiss = () => {
    // Just dismiss without retrying
    setHasError(false);
  };

  return (
    <AlertPopover
      show={hasError}
      message={errorMessage}
      icon="📍"
      variant="danger"
      onRetry={handleRetry}
      onDismiss={handleDismiss}
    />
  );
};

export function App() {
  const [showDebugOverlay, setShowDebugOverlay] = useState(
    () => localStorage.getItem("showDebugOverlay") === "true",
  );
  const [initialHeight, setInitialHeight] = useState(window.innerHeight); // Capture initial available height

  // Update height on actual window resize (not keyboard)
  useEffect(() => {
    let lastWidth = window.innerWidth;
    let lastHeight = window.innerHeight;

    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;

      // Update if width changed (rotation, window resize) OR height increased (keyboard closed)
      // Don't update if only height decreased (keyboard opened)
      if (newWidth !== lastWidth || newHeight > lastHeight) {
        lastWidth = newWidth;
        lastHeight = newHeight;
        // Small delay to ensure browser has updated innerHeight
        setTimeout(() => {
          setInitialHeight(window.innerHeight);
        }, 100);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Helper to update debug overlay state and notify listeners
  const updateDebugOverlay = useCallback((show: boolean) => {
    setShowDebugOverlay(show);
    localStorage.setItem("showDebugOverlay", String(show));
    // Dispatch custom event for same-window listeners
    window.dispatchEvent(new CustomEvent("debugOverlayToggled"));
  }, []);

  useEffect(() => {
    // Initialize tracking service in auto mode
    const trackingService = getTrackingService();

    // Start tracking service in auto mode
    trackingService.start("auto").catch((error) => {
      debug.error("Failed to start tracking service:", error);
    });

    // Set up simulation service integration
    const { getSimulationService } = require("./services/simulationService");
    const simulationService = getSimulationService();

    simulationService.setTrackingModeCallback(
      async (mode: "simulation" | "auto") => {
        if (mode === "simulation") {
          await trackingService.setActiveProvider("simulation");
        } else {
          await trackingService.start("auto");
        }
      },
    );

    debug("Tracking service and simulation integration initialized");

    return () => {
      // Clean up tracking service on unmount
      trackingService.destroy();
    };
  }, []);

  useEffect(() => {
    // Prevent default touch behaviors for better map interaction
    document.addEventListener("gesturestart", (e) => e.preventDefault());

    // Keep screen awake during activity (will implement with Wake Lock API later)
    // if ('wakeLock' in navigator) {
    //   // Implement when activity is recording
    // }

    // Listen for localStorage changes to update debug overlay state
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "showDebugOverlay") {
        updateDebugOverlay(e.newValue === "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      document.removeEventListener("gesturestart", (e) => e.preventDefault());
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Global gesture handler for wake lock
  useEffect(() => {
    const handleGesture = () => {
      wakeLockService.handleUserGesture();
    };

    // Listen for any user interaction
    document.addEventListener("click", handleGesture);
    document.addEventListener("touchstart", handleGesture);
    document.addEventListener("keydown", handleGesture);

    return () => {
      document.removeEventListener("click", handleGesture);
      document.removeEventListener("touchstart", handleGesture);
      document.removeEventListener("keydown", handleGesture);
    };
  }, []);

  return (
    <AuthProvider>
      <PreferencesProvider>
        <ThemeProvider>
          <SettingsProvider>
            <QuickIslandProvider>
              <ActivityProvider>
                <div
                  className="app-viewport-container"
                  style={{
                    height: `${initialHeight}px`,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <div className="app">
                    <TextSizeApplier />

                    <MapContainer>
                      <MapSyncWrapper />
                    </MapContainer>

                    <Header />
                    <ActivityBar />

                    <AutoPauseAlert />
                    <GPSErrorAlert />

                    <CameraWrapper />

                    <PersistentQuickIsland />

                    <GlobalSearchEventListener />
                    <SearchBusyIndicator />

                    <DebugConsole isVisible={showDebugOverlay} />

                    <SettingsModal />
                  </div>
                </div>
              </ActivityProvider>
            </QuickIslandProvider>
          </SettingsProvider>
        </ThemeProvider>
      </PreferencesProvider>
    </AuthProvider>
  );
}

export default App;
